<template>
  <div class="sold-serial-report">
    <v-card flat v-if="isLoading">
      <v-card-text>
        <v-progress-linear :indeterminate="true"></v-progress-linear>
      </v-card-text>
    </v-card>
    <div v-else>
      <v-card flat class="report-page-card">
        <ReportViewBar
          v-if="SerialVault"
          title="Serials Marked Sold"
          :start="start"
          :end="end"
          :export-data="exportData"
          :export-fields="exportFields"
        />

        <v-card-text class="report-content">
          <v-card flat outlined class="report-table-card">
            <v-card-title class="report-table-header">
              <div>
                <div class="report-title">Serials Marked Sold</div>
                <div class="report-subtitle">
                  Cost and expected selling value for manually sold serials
                </div>
              </div>

              <v-spacer />

              <div class="summary-grid">
                <div class="summary-box">
                  <div class="summary-label">Serials Sold</div>
                  <div class="summary-value">{{ totalSerials }}</div>
                </div>
                <div class="summary-box">
                  <div class="summary-label">Total Cost</div>
                  <div class="summary-value">{{ totalCost | currency }}</div>
                </div>
                <div class="summary-box">
                  <div class="summary-label">Expected Sales</div>
                  <div class="summary-value">{{ totalSellingPrice | currency }}</div>
                </div>
                <div class="summary-box profit-summary">
                  <div class="summary-label">Expected Margin</div>
                  <div class="summary-value">{{ expectedMargin | currency }}</div>
                </div>
              </div>
            </v-card-title>

            <v-divider />

            <v-card-text class="report-controls no-print">
              <v-text-field
                v-model="search"
                outlined
                rounded
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                label="Search serial, product, user, or description"
              />
            </v-card-text>

            <v-divider />

            <v-card-text class="pa-0">
                <v-data-table
                  :loading="localLoading"
                  dense
                  sort-by="createdAt"
                  :sort-desc="true"
                  :mobile-breakpoint="0"
                  :headers="headers"
                  :items-per-page="-1"
                  hide-default-footer
                  :items="filteredSerials"
                  class="sold-serial-table"
                >
                  <template v-slot:item="{ item }">
                    <tr class="report-row">
                      <td class="text-left">
                        <div class="date-value">{{ item.createdAt | moment("MM/DD/YYYY") }}</div>
                        <div class="cell-subtitle">{{ item.createdAt | moment("hh:mm A") }}</div>
                      </td>
                      <td class="text-left text-capitalize">
                        <span v-if="item.User" class="font-weight-medium">{{ item.User.name }}</span>
                        <span v-else class="empty-value">--</span>
                      </td>
                      <td class="text-left">
                        <div class="product-name">{{ productName(item) }}</div>
                        <div v-if="productId(item)" class="cell-subtitle">ID: {{ productId(item) }}</div>
                      </td>
                      <td class="text-left serial-number">{{ item.imeiNumber }}</td>
                      <td class="text-left">{{ item.message }}</td>
                      <td class="text-right money">{{ serialCost(item) | currency }}</td>
                      <td class="text-right money">{{ sellingPrice(item) | currency }}</td>
                    </tr>
                  </template>

                  <template v-slot:body.append v-if="filteredSerials.length">
                    <tr class="total-row">
                      <td colspan="5" class="text-right font-weight-bold">Total</td>
                      <td class="text-right font-weight-bold">{{ totalCost | currency }}</td>
                      <td class="text-right font-weight-bold">{{ totalSellingPrice | currency }}</td>
                    </tr>
                  </template>

                  <template v-slot:no-data>
                    <div class="empty-state">No marked-sold serials found for this period.</div>
                  </template>
                </v-data-table>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import reportService from "@/modules/ReportView/service.js";

import { mapGetters, mapActions } from "vuex";

export default {
  data() {
    return {
      expanded: [],
      localLoading: false,
      showEmailBtn: false,
      total: 0,
      isLoading: true,
      SerialVault: null,
      start: null,
      end: null,
      search: "",
      headers: [
        {
          text: "Created At",
          align: "left",
          value: "createdAt",
          sortable: false,
          width: 130,
        },
        {
          text: "User",
          align: "left",
          value: "User.name",
          sortable: false,
        },
        {
          text: "Product",
          align: "left",
          value: "SerialVault.Product.name",
          sortable: true,
        },
        {
          text: "Serial Number",
          align: "left",
          value: "imeiNumber",
          sortable: true,
        },
        {
          text: "Description",
          align: "left",
          value: "message",
          sortable: false,
        },
        {
          text: "Cost",
          align: "right",
          value: "SerialVault.cost",
          sortable: true,
        },
        {
          text: "Selling Price",
          align: "right",
          value: "SerialVault.Product.price",
          sortable: true,
        },
      ],
    };
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    filteredSerials() {
      const rows = this.SerialVault || [];
      const term = (this.search || "").trim().toLowerCase();
      if (!term) return rows;

      return rows.filter((item) =>
        [
          item.imeiNumber,
          item.message,
          item.User && item.User.name,
          this.productName(item),
          this.productId(item),
        ].some((value) => String(value || "").toLowerCase().includes(term))
      );
    },
    totalSerials() {
      return this.filteredSerials.length;
    },
    totalCost() {
      return this.filteredSerials.reduce(
        (sum, item) => sum + this.serialCost(item),
        0
      );
    },
    totalSellingPrice() {
      return this.filteredSerials.reduce(
        (sum, item) => sum + this.sellingPrice(item),
        0
      );
    },
    expectedMargin() {
      return this.totalSellingPrice - this.totalCost;
    },
    exportFields() {
      return {
        Date: "date",
        Time: "time",
        User: "user",
        "Product ID": "productId",
        Product: "product",
        "Serial Number": "serialNumber",
        Description: "description",
        Cost: "cost",
        "Selling Price": "sellingPrice",
      };
    },
    exportData() {
      return this.filteredSerials.map((item) => ({
        date: this.$moment(item.createdAt).format("MM/DD/YYYY"),
        time: this.$moment(item.createdAt).format("hh:mm A"),
        user: item.User ? item.User.name : "",
        productId: this.productId(item),
        product: this.productName(item),
        serialNumber: item.imeiNumber,
        description: item.message,
        cost: this.serialCost(item),
        sellingPrice: this.sellingPrice(item),
      }));
    },
  },
  created() {
    this.start = this.$route.query.start;
    this.end = this.$route.query.end;
    this.loadData();
    if (!this.currentBusiness) this.initUser();
  },
  components: {
    ReportViewBar,
  },
  methods: {
    ...mapActions("global", ["initUser", "initBusiness"]),
    product(item) {
      return item && item.SerialVault && item.SerialVault.Product
        ? item.SerialVault.Product
        : null;
    },
    productName(item) {
      const product = this.product(item);
      return product && product.name ? product.name : "--";
    },
    productId(item) {
      const product = this.product(item);
      return product && product.localId ? product.localId : "";
    },
    serialCost(item) {
      return Number(
        item && item.SerialVault ? item.SerialVault.cost || 0 : 0
      );
    },
    sellingPrice(item) {
      const product = this.product(item);
      return Number(product ? product.price || 0 : 0);
    },
    loadData() {
      this.localLoading = true;
      return reportService
        .getMarkItSoldSerial({ start: this.start, end: this.end })
        .then((response) => {
          this.$Progress.finish();
          this.SerialVault = response.data;

          this.isLoading = false;
          this.localLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.sold-serial-report {
  background: #f5f7fb;
  min-height: 100vh;
  padding: 16px;
}
.report-page-card { border-radius: 18px; overflow: hidden; }
.report-content { padding: 20px; }
.report-table-card { border-radius: 16px; overflow: hidden; }
.report-table-header { padding: 18px 20px; gap: 18px; }
.report-title { color: #1f2937; font-size: 18px; font-weight: 800; }
.report-subtitle, .cell-subtitle { color: #6b7280; font-size: 12px; margin-top: 3px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(115px, 1fr)); gap: 8px; }
.summary-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 9px 12px; text-align: right; }
.summary-label { color: #6b7280; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.summary-value { color: #111827; font-size: 15px; font-weight: 800; margin-top: 2px; }
.profit-summary { background: #ecfdf3; border-color: #bbf7d0; }
.profit-summary .summary-value { color: #15803d; }
.report-controls { max-width: 560px; padding: 14px 20px; }
.report-row:hover { background: #f8fafc; }
.date-value, .product-name { color: #334155; font-weight: 700; }
.serial-number { color: #334155; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-weight: 700; }
.money { color: #0f172a; font-weight: 700; }
.empty-value { color: #94a3b8; }
.total-row { background: #f8fafc; }
.empty-state { color: #64748b; padding: 38px 16px; text-align: center; }

@media print {
  container {
    height: 100%;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden;
  }

  .no-print {
    display: none;
  }

  .detailBox {
    overflow: visible;
    height: 100%;
  }

  * {
    background: 0 0 !important;
    color: #000 !important;
    box-shadow: none !important;
    text-shadow: none !important;
  }
}

@media (max-width: 900px) {
  .report-table-header { align-items: flex-start; flex-direction: column; }
  .summary-grid { grid-template-columns: repeat(2, minmax(140px, 1fr)); width: 100%; }
}

@media (max-width: 560px) {
  .sold-serial-report { padding: 0; }
  .report-content { padding: 10px; }
  .summary-grid { grid-template-columns: 1fr; }
  .report-controls { max-width: none; }
}

.paidImg {
  position: absolute;
  left: 40%;
  top: 10%;
}

.signature {
  margin-top: 100px;
}

.center {
  background-color: white;
  margin: auto;
  width: 100%;
}

/* @media print {
  .detailBox * {
    overflow: visible;
    height: 100%;
    page-break-before: always;
  }
}
.detailBox {
  overflow: auto;
  height: 600px;
} */
</style>
